import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Link2, Link2Off } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const Cell = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">{label}</p>
    <p className="text-sm text-[#111827]">{value || "—"}</p>
  </div>
);

const Sig = ({ label, sig, ack }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">{label}</p>
    {sig?.file_id ? (
      <>
        <img src={fileUrl(sig.file_id)} alt={label} className="h-10 bg-white rounded border border-[#E5E7EB]" />
        <p className="text-[11px] text-[#6B7280]">
          {sig.signer_name || "signed"}{sig.signed_at ? ` · ${new Date(sig.signed_at).toLocaleString()}` : ""}
        </p>
      </>
    ) : (
      <p className="text-sm text-[#111827]">{ack || "Not signed"}</p>
    )}
  </div>
);

/** Toggle button rendered inside the actions cell of a row. */
export const ChainToggle = ({ recordId, open, onToggle }) => (
  <button onClick={onToggle} data-testid={`chain-toggle-${recordId}`} type="button"
    title="Chain of custody" className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]">
    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
  </button>
);

/** Chain-of-custody strip, rendered as a full-width expanded row. */
export const ChainOfCustody = ({ module, recordId, onOpenPartner }) => {
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/chain/${module}/${recordId}`)
      .then(({ data }) => { if (alive) setChain(data); })
      .catch((e) => { if (alive && e?.response?.status !== 404) toast.error(errMsg(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [module, recordId]);

  const d = chain?.data || {};
  const isTrace = chain?.module === "traceability";

  return (
        <div className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-left"
          data-testid={`chain-panel-${recordId}`}>
          {loading && <p className="text-xs text-[#6B7280]">Loading chain of custody…</p>}
          {!loading && chain && !chain.linked && (
            <p className="text-xs text-[#6B7280] flex items-center gap-1.5" data-testid={`chain-none-${recordId}`}>
              <Link2Off className="h-3.5 w-3.5" />
              No {isTrace === false ? "sample" : "traceability"} entry linked yet — matching happens on Sample ID or ULID.
            </p>
          )}
          {!loading && chain?.linked && (
            <>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] uppercase tracking-[0.1em] text-[#002FA7] flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" />
                  {isTrace ? "Chain of custody" : "Linked sample"}
                </p>
                <button onClick={() => onOpenPartner?.(chain)} data-testid={`chain-open-${recordId}`}
                  className="text-xs text-[#002FA7] hover:underline">Open full entry</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {isTrace ? (
                  <>
                    <Cell label="ULID" value={d.uhid} />
                    <Cell label="Nature of sample" value={d.nature_of_sample} />
                    <Cell label="Received" value={[d.received_on, d.time_of_receipt].filter(Boolean).join(" ")} />
                    <Cell label="Delivered from" value={d.delivered_from} />
                    <Cell label="Delivered by" value={d.delivered_by} />
                    <Cell label="Delivered to" value={d.delivered_to} />
                    <Sig label="Attender" sig={d.attender_signature} ack={d.attender_ack} />
                    <Sig label="Receiving technician" sig={d.technician_signature} ack={d.technician_ack} />
                    {d.condition_on_receipt && (
                      <div className="col-span-2 sm:col-span-4">
                        <Cell label="Condition on receipt" value={d.condition_on_receipt} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Cell label="Sample ID" value={d.sample_id} />
                    <Cell label="Patient" value={d.patient_name} />
                    <Cell label="ULID" value={d.patient_id} />
                    <Cell label="Test" value={d.test_name} />
                    <Cell label="Sample type" value={d.sample_type} />
                    <Cell label="Status" value={d.status} />
                    <Cell label="Received" value={d.received_time} />
                    <Cell label="Technician" value={d.technician} />
                  </>
                )}
              </div>
            </>
          )}
        </div>
  );
};
